'use client';

import { useMemo } from 'react';
import { useAppDispatch } from '@/store/hook';
import { startGlobalLoading, stopGlobalLoading } from '@/features/ui/uiLoadingSlice';
import { openModal, closeModal } from '@/features/ui/uiModalSlice';

type UseShareActionsOptions = {
  url?: string; // 要分享/複製的網址
  text?: string; // 分享到 LINE 時附加文字
};

export const useShareActions = ({ url, text }: UseShareActionsOptions) => {
  const dispatch = useAppDispatch();

  const shareUrl = useMemo(() => url?.trim() ?? '', [url]);
  const shareText = text ?? '';

  const hasUrl = !!shareUrl;

  const handleShareToFB = () => {
    if (!hasUrl) return;
    const fbUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`;
    if (typeof window !== 'undefined') {
      window.open(fbUrl, '_blank');
    }
  };

  const handleShareToLine = () => {
    if (!hasUrl) return;
    const message = shareText ? `${shareText} ${shareUrl}` : shareUrl;
    const lineUrl = `https://line.me/R/msg/text/?${encodeURIComponent(message)}`;
    if (typeof window !== 'undefined') {
      window.open(lineUrl, '_blank');
    }
  };

  const handleCopy = async () => {
    if (!hasUrl) return;

    if (typeof navigator === 'undefined' || !navigator.clipboard) {
      alert('此環境不支援自動複製，請手動複製網址');
      return;
    }

    dispatch(startGlobalLoading());
    try {
      await navigator.clipboard.writeText(shareUrl);

      /** 固定成功訊息 → 不再支援客製化 */
      dispatch(
        openModal({
          variant: 'success',
          title: '已成功複製連結',
        })
      );

      setTimeout(() => dispatch(closeModal()), 2000);
    } catch (err) {
      console.error('複製失敗', err);
      alert('無法複製連結，請手動複製');
    } finally {
      dispatch(stopGlobalLoading());
    }
  };

  return {
    shareUrl,
    shareText,
    hasUrl,
    handleShareToFB,
    handleShareToLine,
    handleCopy,
  };
};
